<template>
  <aside class="relative z-50 mb-6 sidebar">
    <div
      class="px-6 py-5 bg-white rounded-md shadow-lg lg:px-10 lg:py-8 sidebar-inner"
    >
      <header class="flex items-center justify-between mb-5 lg:mb-8">
        <a title="Go to Ravenol Website" rel="noopener" :href="[ $i18n.locale === 'de' ? 'https://www.ravenol.de/' : 'https://www.ravenol.de/en/' ]" target="_blank">
          <img src="@/assets/img/logo-ravenol.png" class="mt-1" title="Ravenol Logo" />
        </a>
        <div class="pipe"></div>
        <div class="text-2xl font-bold leading-none font-eurostile text-darkblue oilfinder" :class="[ $i18n.locale === 'zh-CN' || $i18n.locale === 'en' ? 'whitespace-no-wrap' : '' ]">
          {{ $t("common.Oilfinder") }}
        </div>
      </header>
      <div class="px-3 py-1 pl-6 mb-2 -ml-6 lg:-ml-10 lg:pl-10 md:mb-3 title">
        {{ $t("sidebar.selectCar") }}
      </div>

      <section class="mt-5 md:mt-6 md:mb-4" v-if="!isLanguageRestricted">
        <base-select
            :placeholder="$t('sidebar.countryOfRegistrationPlaceholder')"
            :label="$t('sidebar.countryOfRegistration')"
            :options="advancedSearchAvailableCountriesOptions"
            :disabled="advancedSearchAvailableCountriesOptions.length < 1"
            :loading="advancedSearchAvailableCountriesOptions.length < 1"
            :value="selectedAdvancedSearchCountry"
            target="countryOfRegistration"
            @selected="setState"
            :clearable="true"
            :searchable="false"
          ></base-select>
      </section>

      <component :is="advancedSearchComponent" />

      <section class="flex items-center mt-5 md:mt-6 md:mb-4">
        <hr class="flex-auto w-auto" />
        <div class="mx-4 text-sm text-center text-gray-500">
          {{ $t("sidebar.or") }}
        </div>
        <hr class="flex-auto w-auto" />
      </section>

      <section>
        <base-search
          :placeholder="$t('sidebar.searchPlaceholder')"
          :label="$t('sidebar.search')"
          @selected="handleSubmit"
        ></base-search>
      </section>

      <section class="flex items-center mt-5 md:mt-6 md:mb-4">
        <hr class="flex-auto w-auto" />
        <div class="mx-4 text-sm text-center text-gray-500">
          {{ $t("sidebar.or") }}
        </div>
        <hr class="flex-auto w-auto" />
      </section>

      <section>
        <base-select
          :placeholder="$t('sidebar.selectVehicle')"
          :label="$t('sidebar.vehicle')"
          :options="vehicles"
          :disabled="vehicles.length < 1"
          :loading="vehicles.length < 1"
          :value="selected.vehicle"
          target="vehicle"
          @selected="setState"
          :clearable="false"
        ></base-select>
        <base-select
          :placeholder="$t('sidebar.selectMake')"
          :label="$t('sidebar.make')"
          :options="makers"
          :disabled="makers.length < 1 ? true : false || selected.vehicle ? false : true"
          :loading="makers.length < 1 && selected.vehicle ? true : false"
          :value="selected.make"
          :hasImages="true"
          target="make"
          @selected="setState"
        ></base-select>
        <base-select
          :placeholder="$t('sidebar.selectModel')"
          :label="$t('sidebar.model')"
          :options="models"
          :disabled="models.length < 1 ? true : false || selected.make ? false : true"
          :loading="models.length < 1 && selected.make ? true : false"
          :value="selected.model"
          target="model"
          @selected="setState"
        ></base-select>
        <base-select
          :placeholder="$t('sidebar.selectType')"
          :label="$t('sidebar.type')"
          :options="types"
          :disabled="types.length < 1 ? true : false || selected.model ? false : true"
          :loading="types.length < 1 && selected.model ? true : false"
          :value="selected.type"
          target="type"
          @selected="setState"
        ></base-select>
        <base-button class="w-auto mt-3" :loading="isLoading" :disabled="!allSelected" @click="handleSubmit(selected.type)">
          {{ $t("sidebar.search") }}
        </base-button>
      </section>
    </div>
  </aside>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import { updateUrlParams, deleteUrlParam } from '@/util/url-params';
import { pascalCase } from 'change-case';

import BaseSelect from '@/components/BaseSelect.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseSearch from '@/components/BaseSearch.vue';

export default {
  components: {
    BaseSelect,
    BaseButton,
    KbaSearch: () => import('@/components/KBASearch.vue'),
    VinSearch: () => import('@/components/VINSearch.vue'),
    LicensePlateSearch: () => import('@/components/LicensePlateSearch.vue'),
    BaseSearch
  },
  data: () => ({
    isLoading: false
  }),
  computed: {
    ...mapState({
      vehicles: state => state.apiData.vehicles,
      makers: state => state.apiData.makers,
      models: state => state.apiData.models,
      types: state => state.apiData.types,
      selected: state => state.apiData.selected,
      language: state => state.language,
      isLanguageRestricted: state => state.language.restricted
    }),
    ...mapState('advancedSearch', {
      selectedAdvancedSearchCountry: state => state.selectedCountry,
      advancedSearchAvailableCountries: 'availableCountries'
    }),
    ...mapGetters('advancedSearch', {
      advancedSearchAvailableCountriesOptions: 'availableCountriesOptions'
    }),
    allSelected () {
      return this.selected.vehicle && this.selected.make && this.selected.model && this.selected.type;
    },
    currentSearchType () {
      return this.advancedSearchAvailableCountries.find(country => country.code === this.selectedAdvancedSearchCountry)?.searchType;
    },
    advancedSearchComponent () {
      if (!this.currentSearchType) return null;
      const componentName = `${pascalCase(this.currentSearchType)}Search`;
      return this.$options.components[componentName] ? componentName : null;
    }
  },
  async mounted () {
    if (this.isLanguageRestricted && this.language.locale === 'en') {
      this.SET_SELECTED_COUNTRY('GB');
    } else if (this.isLanguageRestricted && this.language.locale === 'es') {
      this.SET_SELECTED_COUNTRY('ES');
    }
    await this.GET_VEHICLES();
    this.getResultsByUrl();
  },
  methods: {
    ...mapActions(['GET_VEHICLES', 'GET_MAKERS', 'GET_MODELS', 'GET_TYPES', 'UNSET_SELECTED', 'GET_RESULTS', 'SET_LOADING', 'UPDATE_URL_PARAMS']),
    ...mapMutations(['SET_SELECTED']),
    ...mapActions('advancedSearch', ['SET_SELECTED_COUNTRY']),
    async setState (prop) {
      this.SET_SELECTED({ key: prop.target, value: prop.value });
      deleteUrlParam('selectedType');
      switch (prop.target) {
        case 'vehicle':
          await this.UNSET_SELECTED(
            {
              selected: ['make', 'model', 'type'],
              data: ['makers', 'models', 'types', 'results']
            }
          );
          await this.GET_MAKERS();
          break;
        case 'make':
          await this.UNSET_SELECTED(
            {
              selected: ['model', 'type'],
              data: ['models', 'types', 'results']
            }
          );
          await this.GET_MODELS();
          break;
        case 'model':
          await this.UNSET_SELECTED(
            {
              selected: ['type'],
              data: ['types', 'results']
            }
          );
          await this.GET_TYPES();
          break;
        case 'countryOfRegistration':
          this.SET_SELECTED_COUNTRY(prop.value);
          break;
        case 'type':
          break;
        default:
          break;
      }
    },
    async handleSubmit (typeId) {
      this.isLoading = true;
      await this.UNSET_SELECTED(
        {
          selected: [],
          data: ['results']
        }
      );
      await this.GET_RESULTS(typeId);
      updateUrlParams('selectedType', typeId);
      this.isLoading = false;
    },
    getResultsByUrl () {
      const URLparams = new URLSearchParams(location.search);
      if (URLparams.get('selectedType')) {
        this.GET_RESULTS(URLparams.get('selectedType'));
      }
    }
  }
};
</script>

<style scoped lang="postcss">
.pipe {
  @apply h-8 bg-gray-300 block mx-4;
  width: 3px;
  transform: skewX(-16deg);
}
header .oilfinder {
  font-size: clamp(17px, 1.5rem, 1.75rem);
}
.title {
  @apply block text-lg font-bold font-eurostile text-white;
  background: linear-gradient(
    145deg,
    theme("colors.darkblue") 0%,
    theme("colors.lightblue") 100%
  );
  clip-path: polygon(0 0, 100% 0%, calc(100% - 15px) 100%, 0% 100%);
}
.sidebar-inner::before {
  @apply inset-0 absolute pointer-events-none;
  content: "";
  clip-path: polygon(0 0, 100% 0%, 0 100%, 0% 100%);
  background: linear-gradient(
    145deg,
    theme("colors.lightblue") 0%,
    theme("colors.darkblue") 100%
  );
  z-index: -1;
  bottom: 1rem;
  right: -10rem;

  /* Set negativ to offset padding */
  top: -1.5rem;
  left: -1.5rem;

  @screen lg {
    top: -2rem;
    left: -2.5rem;
  }

  @screen md {
    right: 0;
    bottom: -8rem;
  }
}
</style>
